import styles from './sales-person-overview.module.scss';
import { Col, Row } from 'reactstrap';
import PageTitle from '../page-title/page-title';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { makeRequest, useGetRequestNewHook } from '../requestUtils';
import WebkuaTable from '../webkua-table/webkua-table';
import { useNavigate } from 'react-router-dom';
import { ValueFormatterParams } from 'ag-grid-community';
import { AvatarOnlyBorderSecondary, Box, Card, CardContent, CardHeader, Container, CreateHeaders, currencyHeader, dateHeader, Dialog, DialogActions, DialogContent, DialogTitle, ErrorButton, 
  GrayOutlinedButton, HeaderSizeEnum, Icon, MenuItem, OrderTypesEnum, PrimaryButton, Select, Tab, Tabs, textHeader, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@frontend-workspace/react-shared-lib';
import { ProposalStateEnum } from '../_enums/proposal-state';
import { IProposal, IProposals } from '../_models/sales-overviews';
import SalesPersonSkeleton from './sales-person-overview-skeleton';
import { SignalRContext,} from '../signalr-context/signalr-context';
import { OverviewContent } from '../overview/overview';
import { getInitialsFromEmail } from '../helpers';

/* eslint-disable-next-line */
export interface SalesPersonOverviewContentProps {
  salesOverview: IProposals,
  getData: () => void,
  isLoading: boolean
}
enum FilterEnum {
  ACTIVE = "active",
  ACCEPTED = "accepted",
  REJECTED_EXPIRED = "rejectedExpired"
}

export function SalesPersonOverview(props: any) {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { t } = useTranslation();

  return <>
    <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px', paddingTop: '20px' }}>
      <Tabs
        value={selectedTab}
        variant="scrollable"
        scrollButtons="auto">
        <Tab value={0} onClick={() => setSelectedTab(0)} label={t("app.offers")} />
        <Tab value={1} onClick={() => setSelectedTab(1)} label={t("standard.overview")} />
      </Tabs>
    </Box>
    {(selectedTab === 0 && <SalesPersonOverviewContainer />)}
    {selectedTab === 1 && <OverviewContent />}
  </>;
};
export function SalesPersonOverviewContainer(props: any) {
  const { data: salesOverview, isLoading, getData }: { data: IProposals, isLoading: boolean, getData: () => void } = useGetRequestNewHook('overviews/sales', { proposals: [] }, []);
  const connection = useContext(SignalRContext);

  const handleRefresh = (e: any) => {
    if (e.OrderType === OrderTypesEnum.SendSaleProposalOrder) {
      getData();
    }
  }

  useEffect(() => {
    connection?.on("refresh", handleRefresh);
    return () => {
      connection?.off("refresh");
    }
  }, [connection]);

  return !isLoading ? <SalesPersonOverviewContent salesOverview={salesOverview} getData={getData} isLoading={isLoading} /> : <SalesPersonSkeleton />;
}

export function SalesPersonOverviewContent(props: SalesPersonOverviewContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const salesOverview = props.salesOverview;

  const [selectedSeller, setSelectedSeller] = useState<string>(salesOverview.currentUserEmail);
  const [selectedStatus, setSelectedStatus] = useState<FilterEnum>(FilterEnum.ACTIVE);
  const [filteredProposals, setFilteredProposals] = useState<IProposal[]>([]);

  const handleSellerChange = (event: any) => {
    setSelectedSeller(event.target.value);
  };

  const handleStatusChange = (event: any, newStatus: FilterEnum) => {
    if (newStatus !== null) {
      setSelectedStatus(newStatus);
    }
  };

  useEffect(() => {
    const filtered = salesOverview.proposals.filter(proposal => {
      const sellerMatch = selectedSeller ? proposal.seller === selectedSeller : true;
      const statusMatch = selectedStatus === "active" ? proposal.state === ProposalStateEnum.PENDING || proposal.state === ProposalStateEnum.COUNTER_PROPOSAL || proposal.state === ProposalStateEnum.DRAFT
        : selectedStatus === "accepted" ? proposal.state === ProposalStateEnum.ACCEPTED
          : proposal.state === ProposalStateEnum.REJECTED || proposal.state === ProposalStateEnum.EXPIRED;
      return sellerMatch && statusMatch;
    });
    setFilteredProposals(filtered);
  }, [selectedSeller, selectedStatus, salesOverview.proposals]);

  const sellerDropdown = !props.isLoading && (
    <div className='d-flex flex-wrap mt-3'>
      <Select
        value={selectedSeller || ''}
        onChange={handleSellerChange}
        displayEmpty
        label={t("simpleTable.seller")}
        sx={{ minWidth: 150 }}
        className='me-3 mb-3 mb-sm-0'>
        <MenuItem value="">
          {t("simpleTable.selectAll")}
        </MenuItem>
        {salesOverview.sellers?.map(seller => (
          <MenuItem key={seller.email} value={seller.email}>
            {seller.name}
          </MenuItem>
        ))}
      </Select>
      <div className='d-flex flex-column'>
        <div><label>{t("simpleTable.show")}:</label></div>
        <div>
          <ToggleButtonGroup
            color="secondary"
            exclusive
            size="small"
            value={selectedStatus}
            onChange={handleStatusChange}
            className='me-3 mb-3 mb-sm-0'>
            <ToggleButton value="active" className='minToggleButtonWidth'>{t("proposalState.Active")}</ToggleButton>
            <ToggleButton value="accepted" className='minToggleButtonWidth'>{t("proposalState.Accepted")}</ToggleButton>
            <ToggleButton value="rejectedExpired" className='minToggleButtonWidth'>{`${t("proposalState.Rejected")}/${t("proposalState.Expired")}`}</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>
    </div>
  );

  const deleteRenderer = (params: ValueFormatterParams) => {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleConfirmDelete = () => {
      makeRequest(`proposals/${params.value}`, "DELETE").then(() => {
        props.getData();
        handleClose();
      });
    };
    return (<>
      <ErrorButton onClick={handleOpen} variant='outlined' size='small'>
        {t("standard.delete")}
      </ErrorButton>
      <DeleteOfferDialog
        open={open}
        onClose={handleClose}
        handleClose={handleClose}
        handleConfirmDelete={handleConfirmDelete}
        company={params.data.companyName}
        proposal={params.data.proposalNumber}
      />
    </>);
  };

  const proposalStateCellRenderer = (props: any) => {
    const getColor = (state: ProposalStateEnum) => {
      switch (state) {
        case ProposalStateEnum.DRAFT:
          return '#666';
        case ProposalStateEnum.PENDING:
          return '#2C6C8F';
        case ProposalStateEnum.COUNTER_PROPOSAL:
          return '#2C6C8F';
        case ProposalStateEnum.ACCEPTED:
          return '#5A9C07';
        case ProposalStateEnum.REJECTED:
          return '#A30014';
        case ProposalStateEnum.EXPIRED:
          return '#666';
        default:
          return '#666';
      }
    };
    return (
      <div style={{ color: getColor(props.value) }}>
        {t(`proposalState.${props.value}`)}
      </div>
    );
  };
  const counterProposalCount = salesOverview.proposals.filter((p) => p.state === ProposalStateEnum.COUNTER_PROPOSAL && p.seller === selectedSeller).length;
  const acceptedCount = salesOverview.proposals.filter((p) => p.state === ProposalStateEnum.ACCEPTED && p.seller === selectedSeller).length;
  const pendingCount = salesOverview.proposals.filter((p) => p.state === ProposalStateEnum.PENDING && p.seller === selectedSeller).length;
  const expiredCount = salesOverview.proposals.filter((p) => (p.state === ProposalStateEnum.EXPIRED || p.state === ProposalStateEnum.REJECTED) && p.seller === selectedSeller).length;

  const handleRowClicked = (rowData: any) => {
    if (rowData.event.target.tagName !== 'BUTTON') {
      const selectedCompanyNumber = rowData.data.companyNumber;
      const selectedProposalId = rowData.data.id;
      navigate(`/customers/${selectedCompanyNumber}/proposal/${selectedProposalId}`);
    }
  };

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box
            component="div"
            className="pb-5">
            <PageTitle title={t("app.offers")} subtitle='' />
            {props.isLoading ? <SalesPersonSkeleton /> :
              <>
                <Row className='mb-4' >
                  <Col md={6} xxl={3} onClick={() => setSelectedStatus(FilterEnum.ACTIVE)}>
                    <Card className='mb-4' >
                      <CardHeader
                        avatar={
                          <AvatarOnlyBorderSecondary>
                            <Icon className='material-icons-outlined'>forum</Icon>
                          </AvatarOnlyBorderSecondary>
                        }
                        title={t('standard.answer')}
                      />
                      <CardContent className='pt-0'>
                        <div className='d-flex mb-3'>
                          <Typography
                            variant="h1"
                            component="div"
                            className='fw-bold'>
                            {counterProposalCount}
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Col>
                  <Col md={6} xxl={3} onClick={() => setSelectedStatus(FilterEnum.ACTIVE)}>
                    <Card className='mb-4'>
                      <CardHeader
                        avatar={
                          <AvatarOnlyBorderSecondary>
                            <Icon className='material-icons-outlined'>hourglass_top</Icon>
                          </AvatarOnlyBorderSecondary>
                        }
                        title={t('standard.waitingForResponse')}
                      />
                      <CardContent className='pt-0'>
                        <div className='d-flex mb-3'>
                          <Typography
                            variant="h1"
                            component="div"
                            className='fw-bold'>
                            {pendingCount}
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Col>
                  <Col md={6} xxl={3} onClick={() => setSelectedStatus(FilterEnum.ACCEPTED)}>
                    <Card className='mb-4'>
                      <CardHeader
                        avatar={
                          <AvatarOnlyBorderSecondary>
                            <Icon>task_alt</Icon>
                          </AvatarOnlyBorderSecondary>
                        }
                        title={t('standard.approvedOffers')}
                      />
                      <CardContent className='pt-0'>
                        <div className='d-flex mb-3'>
                          <Typography
                            variant="h1"
                            component="div"
                            className='fw-bold'>
                            {acceptedCount}
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Col>
                  <Col md={6} xxl={3} onClick={() => setSelectedStatus(FilterEnum.REJECTED_EXPIRED)}>
                    <Card className='mb-4'>
                      <CardHeader
                        avatar={
                          <AvatarOnlyBorderSecondary>
                            <Icon className='material-icons-outlined'>archive</Icon>
                          </AvatarOnlyBorderSecondary>
                        }
                        title={t('standard.expiredOffers')}
                      />
                      <CardContent className='pt-0'>
                        <div className='d-flex mb-3'>
                          <Typography
                            variant="h1"
                            component="div"
                            className='fw-bold'>
                            {expiredCount}
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Col>
                </Row>

                <WebkuaTable
                  isLoading={props.isLoading}
                  headers={CreateHeaders([
                    textHeader("proposalNumber", t("standard.offerNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.MEDIUM),
                    textHeader("companyName", t("standard.companyName"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("seller", t("standard.seller"), HeaderSizeEnum.SMALL, {
                      renderer: (params: ValueFormatterParams) => {
                        const initials = getInitialsFromEmail(params.data?.seller);
                        return (
                          <Tooltip title={params.data?.seller || ''}>
                            <span>{initials}</span>
                          </Tooltip>
                        );
                      }
                    }),
                    dateHeader("sent", t("standard.sent")),
                    dateHeader("valideUntil", t("standard.deadline")),

                    dateHeader("lastContact", t("standard.lastContact")),
                    textHeader("licenses", t("standard.products"), HeaderSizeEnum.LARGE, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                    currencyHeader("recurringPrice", t("standard.recurringPrice"), HeaderSizeEnum.EXTRA_LARGE, {
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                    textHeader("comment", t("standard.comment"), HeaderSizeEnum.EXTRA_LARGE),
                    textHeader("state", t("standard.state"), HeaderSizeEnum.EXTRA_LARGE, {
                      renderer: proposalStateCellRenderer
                    }),
                    textHeader("id", "", HeaderSizeEnum.MEDIUM, {
                      renderer: deleteRenderer,
                      cellStyle: { justifyContent: 'right' },
                      headerClass: "ag-right-aligned-header"
                    }),
                  ])}
                  data={filteredProposals}
                  customComponent={sellerDropdown}
                  onRowClicked={handleRowClicked}
                />
              </>}
          </Box>
        </Col>
      </Row>
    </Container>
  );
};



function DeleteOfferDialog(props: {
  open: boolean,
  onClose: () => void,
  handleClose: () => void,
  handleConfirmDelete: () => void,
  company: string,
  proposal: string
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={props.open}
      onClose={props.handleClose}
      maxWidth='sm'
      fullWidth>
      <DialogTitle className='mb-3'>
        {t("standard.areYouSureYouWantToDelete")}
      </DialogTitle>
      <DialogContent>
        <Typography
          component="div"
          variant='subtitle3'>
          {props.company} {props.proposal ? `- ${props.proposal}` : ""}
        </Typography>
      </DialogContent>
      <DialogActions>
        <ErrorButton
          variant="contained"
          size='large'
          onClick={props.handleConfirmDelete}
          className='minButtonWidth'>
          {t("standard.deleteOffer")}
        </ErrorButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.handleClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
};

export default SalesPersonOverview;
